/**
 * CostEstimateModal Component
 *
 * Modal showing cost estimate breakdown before processing documents.
 */

import { X, DollarSign, Clock, AlertTriangle, FileText, CheckCircle } from 'lucide-react';
import type { CostEstimate } from '../../types';

interface CostEstimateModalProps {
  estimate: CostEstimate;
  onConfirm: () => void;
  onCancel: () => void;
  isOpen: boolean;
  isSubmitting?: boolean;
}

function CostBreakdownRow({
  label,
  amount,
  description,
}: {
  label: string;
  amount: number;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm text-gray-700 dark:text-gray-300">{label}</p>
        {description && <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>}
      </div>
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">${amount.toFixed(2)}</span>
    </div>
  );
}

export function CostEstimateModal({
  estimate,
  onConfirm,
  onCancel,
  isOpen,
  isSubmitting = false,
}: CostEstimateModalProps) {
  if (!isOpen) return null;

  const formatDuration = (minutes: number): string => {
    if (minutes < 1) return 'Less than a minute';
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                <DollarSign size={20} className="text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Cost Estimate</h2>
            </div>
            <button
              onClick={onCancel}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Document info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg mb-4">
              <FileText size={20} className="text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {estimate.page_count} page{estimate.page_count !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {estimate.criteria_count} criteria to evaluate
                </p>
              </div>
            </div>

            {/* Cost breakdown */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-3 mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cost Breakdown</h3>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                <CostBreakdownRow
                  label="Text Extraction"
                  amount={estimate.extraction_cost}
                  description="PDF parsing and chunking"
                />
                {estimate.ocr_cost > 0 && (
                  <CostBreakdownRow
                    label="OCR Processing"
                    amount={estimate.ocr_cost}
                    description="Scanned page recognition"
                  />
                )}
                {estimate.vision_verification_cost > 0 && (
                  <CostBreakdownRow
                    label="Vision Verification"
                    amount={estimate.vision_verification_cost}
                    description="Claude Vision for complex pages"
                  />
                )}
                <CostBreakdownRow
                  label="AI Analysis"
                  amount={estimate.analysis_cost}
                  description="Criteria evaluation with citations"
                />
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between py-2 mb-4">
              <span className="text-base font-medium text-gray-900 dark:text-gray-100">Total Estimated Cost</span>
              <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                ${estimate.total_estimated_cost.toFixed(2)}
              </span>
            </div>

            {/* Estimated duration */}
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
              <Clock size={16} />
              <span>
                Estimated processing time: {formatDuration(estimate.estimated_duration_minutes)}
              </span>
            </div>

            {/* Approval warning */}
            {estimate.approval_required && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
                <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Approval Required</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    This cost exceeds your authorization limit. An administrator will need to
                    approve this request before processing can begin.
                  </p>
                </div>
              </div>
            )}

            {/* Additional breakdown if provided */}
            {Object.keys(estimate.breakdown).length > 0 && (
              <details className="mb-4">
                <summary className="text-sm text-primary-600 dark:text-primary-400 cursor-pointer hover:text-primary-700 dark:hover:text-primary-300">
                  View detailed breakdown
                </summary>
                <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
                  <dl className="space-y-1 text-xs">
                    {Object.entries(estimate.breakdown).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <dt className="text-gray-600 dark:text-gray-400">{key}</dt>
                        <dd className="text-gray-900 dark:text-gray-100">
                          {typeof value === 'number' ? `$${value.toFixed(4)}` : value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </details>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            <button
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : estimate.approval_required ? (
                <>
                  <AlertTriangle size={16} />
                  Request Approval
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Confirm & Process
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
